import * as authenticate from 'api/authentificationAPI';

// Fonction pour obtenir un cookie par son nom
export function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
}

// Fonction pour définir un cookie
export function setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
}

// Vérifie le cookie local pour l'email encodé
export async function checkLocalCookie() {
    const encodedEmail = getCookie('encodedEmail');
    if (encodedEmail) {
        try {
            const response = await authenticate.decodeEmail(encodedEmail);
            if (response.data) {
                return response.data;
            }
        } catch (error) {
            console.error("Erreur lors du décodage de l'email :", error);
            // Ne pas lancer d'erreur, simplement retourner null pour permettre la tentative SSO
            return null;
        }
    }
    return null;
}

// Vérifie le SSO
export async function checkSSO() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // Timeout de 10 secondes
    
    try {
        const response = await fetch('https://bes-sso.altitudeinfra.fr/api/zhhkam_ajax_check', {
            method: 'GET',
            credentials: 'include',
            signal: controller.signal
        });
        clearTimeout(timeoutId);
        const data = await response.text();
        if (data !== 'nok') {
            const encodeResponse = await authenticate.encodeEmail(data);
            const encodedEmail = encodeResponse.data;
            setCookie('encodedEmail', encodedEmail, 365);
            return data;
        }
    } catch (error) {
        clearTimeout(timeoutId);
        console.error('Erreur lors de la vérification SSO :', error);
        // Ne pas lancer d'erreur, simplement retourner null pour permettre la connexion manuelle
        return null;
    }
    return null;
}

// Fonction pour gérer la connexion
export async function login(email, password) {
    try {
        const response = await authenticate.sendAuthentification(email, password);
        const responseData = response.data;
        
        if (responseData === 'ok') {
            const encodedResponse = await authenticate.encodeEmail(email);
            const encodedEmail = encodedResponse.data;
            setCookie('encodedEmail', encodedEmail, 365);
            return { success: true, email: email };
        } else if (responseData === 'nok') {
            return { success: false, error: 'INVALID_CREDENTIALS', message: 'Email ou mot de passe incorrect.' };
        } else if (typeof responseData === 'string') {
            // Vérifier si la réponse contient un message d'erreur
            const lowerData = responseData.toLowerCase();
            if (lowerData.includes('erreur') || lowerData.includes('mauvais login') || lowerData.includes('incorrect')) {
                return { success: false, error: 'INVALID_CREDENTIALS', message: 'Email ou mot de passe incorrect.' };
            }
            // Autre réponse texte inattendue
            return { success: false, error: 'UNKNOWN_RESPONSE', message: `Réponse inattendue : ${responseData}` };
        } else {
            return { success: false, error: 'UNKNOWN_RESPONSE', message: 'Réponse inattendue du serveur.' };
        }
    } catch (error) {
        console.error("Erreur lors de l'authentification :", error);
        
        // Gestion des différents types d'erreurs
        if (error.response) {
            const errorData = error.response.data;
            
            // Si le serveur retourne un objet avec error et message
            if (errorData && errorData.error && errorData.message) {
                return { 
                    success: false, 
                    error: errorData.error, 
                    message: errorData.message 
                };
            }
            
            // Gestion des codes de statut HTTP
            if (error.response.status === 400) {
                return { 
                    success: false, 
                    error: 'INVALID_INPUT', 
                    message: 'Les données saisies sont invalides. Veuillez vérifier votre email et mot de passe.' 
                };
            }
            
            if (error.response.status === 401) {
                return { 
                    success: false, 
                    error: 'INVALID_CREDENTIALS', 
                    message: 'Email ou mot de passe incorrect.' 
                };
            }
            
            if (error.response.status === 403) {
                return { 
                    success: false, 
                    error: 'ACCESS_FORBIDDEN', 
                    message: 'Accès refusé. Veuillez contacter votre administrateur.' 
                };
            }
            
            if (error.response.status === 429) {
                return {
                    success: false,
                    error: 'RATE_LIMIT',
                    message: 'Trop de tentatives de connexion. Veuillez réessayer dans 15 minutes.'
                };
            }

            if (error.response.status === 502 || error.response.status === 503) {
                return { 
                    success: false, 
                    error: 'SERVICE_UNAVAILABLE', 
                    message: 'Le service d\'authentification est temporairement indisponible. Veuillez réessayer plus tard.' 
                };
            }
            
            if (error.response.status === 504) {
                return { 
                    success: false, 
                    error: 'TIMEOUT', 
                    message: 'Le serveur d\'authentification ne répond pas. Veuillez réessayer dans quelques instants.' 
                };
            }
        }
        
        // Erreurs réseau (pas de réponse du serveur)
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
            return { 
                success: false, 
                error: 'TIMEOUT', 
                message: 'La connexion a expiré. Vérifiez votre connexion internet et réessayez.' 
            };
        }
        
        if (error.code === 'ECONNREFUSED' || error.code === 'ENOTFOUND' || !error.response) {
            return { 
                success: false, 
                error: 'NETWORK_ERROR', 
                message: 'Impossible de se connecter au serveur. Vérifiez votre connexion internet.' 
            };
        }
        
        // Erreur inconnue
        return { 
            success: false, 
            error: 'UNKNOWN_ERROR', 
            message: 'Une erreur inattendue s\'est produite. Veuillez réessayer.' 
        };
    }
}

// Fonction pour obtenir les groupes de sécurité
export async function getSecurityGroups(email) {
    try {
        const response = await authenticate.getSecurityGroups(email);
        return response.data;
    } catch (error) {
        console.error("Erreur lors de la récupération des groupes de sécurité :", error);
        return null;
    }
}